import React, { useState, useCallback, useEffect } from 'react';
import {
  useUnreadNotifications,
  UseUnreadNotificationsResult
} from '../hooks/useUnreadNotifications';
import type { TitleUpdateEvent } from '../services/notificationSSEService';
import '../styles/notificationBadge.css';

/**
 * 通知徽章组件 Props
 */
export interface NotificationBadgeProps {
  /** 点击通知项时的回调 */
  readonly onClickNotification?: (conversationId: number) => void;
  /** 轮询间隔（毫秒） */
  readonly pollingInterval?: number;
   /** 传递刷新函数的 ref（用于外部触发刷新） */
  readonly onRefreshRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  readonly onUnreadUpdate?: () => Promise<void>;
  /** 标题更新回调 */
  readonly onTitleUpdate?: (event: TitleUpdateEvent) => void;
  /** 业务类型，用于过滤通知 */
  readonly businessType?: string;
}

/**
 * 通知徽章组件
 * 显示未读消息数量，点击展开通知列表
 */
export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  onClickNotification,
  pollingInterval = 30000,
  onRefreshRef,
  onUnreadUpdate,
  onTitleUpdate,
  businessType
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    notifications,
    count,
    loading,
    markAsRead,
    markAllAsRead,
    refresh // 获取 refresh 函数
  }: UseUnreadNotificationsResult = useUnreadNotifications({onTitleUpdate, businessType});

  // 包装 refresh，同时触发 onUnreadUpdate
  const handleRefresh = useCallback(async (): Promise<void> => {
    await refresh();
    await onUnreadUpdate?.();
  }, [refresh, onUnreadUpdate]);

  // 将 refresh 函数暴露给外部
  useEffect(() => {
    if (onRefreshRef) {
      onRefreshRef.current = handleRefresh;
    }
    return () => {
      if (onRefreshRef) {
        onRefreshRef.current = null;
      }
    };
  }, [onRefreshRef, handleRefresh]);

  // 新增：通知数量变化时触发未读更新
  useEffect(() => {
    void onUnreadUpdate?.();
  }, [count, onUnreadUpdate]);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleClickItem = useCallback(async (
    taskId: number,
    conversationId: number
  ): Promise<void> => {
    try {
      await markAsRead(taskId);
      await onUnreadUpdate?.(); // 更新未读状态
      onClickNotification?.(conversationId);
      setIsOpen(false);
    } catch (err) {
      console.error('[NotificationBadge] handleClickItem failed:', err);
    }
  }, [markAsRead, onClickNotification, onUnreadUpdate]);

  const handleMarkAllRead = useCallback(async (): Promise<void> => {
    try {
      await markAllAsRead();
      await onUnreadUpdate?.(); // 更新未读状态
    } catch (err) {
      console.error('[NotificationBadge] handleMarkAllRead failed:', err);
    }
  }, [markAllAsRead, onUnreadUpdate]);

  return (
    <div className="notification-container">
      {/* 徽章按钮 */}
      <button
        className="notification-badge"
        onClick={handleToggle}
        title="未读消息"
      >
        <i className="fas fa-bell" />
        {count > 0 && (
          <span className="notification-count">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {/* 下拉面板 */}
      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <span>未读消息 ({count})</span>
            {count > 0 && (
              <button
                className="notification-mark-all-btn"
                onClick={handleMarkAllRead}
              >
                全部已读
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading && notifications.length === 0 && (
              <div className="notification-empty">加载中...</div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="notification-empty">暂无未读消息</div>
            )}

            {notifications.map(item => (
              <div
                key={item.taskId}
                className="notification-item"
                onClick={() => handleClickItem(item.taskId, item.conversationId)}
              >
                <div className="notification-item-title">
                  {item.conversationTitle}
                </div>
                <div className="notification-item-preview">
                  {item.contentPreview}
                </div>
                <div className="notification-item-time">
                  {new Date(item.dateCreated).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};